import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import {
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Grid,
  Paper,
  TextField,
  Typography,
} from "@mui/material";
import ArrowBackIosRoundedIcon from "@mui/icons-material/ArrowBackIosRounded";
import {
  fetchFilteredTree,
  fetchSubRolePerms,
  createSubRole,
  updateSubRole,
} from "redux/actions/RolesAndPermissionsAction";
import { toast } from "react-toastify";

// ─── Helpers ──────────────────────────────────────────────────────────────────

const extractIds = (node) => {
  let ids = [...(node.permissions?.map((p) => p.id) || [])];
  if (node.subModules)
    node.subModules.forEach((sub) => {
      ids = [...ids, ...extractIds(sub)];
    });
  return ids;
};

// ─── Recursive Permission Checkbox Tree ───────────────────────────────────────

const PermissionNode = ({ node, checkedIds, onToggle }) => {
  const allIds = extractIds(node);
  const isChecked =
    allIds.length > 0 && allIds.every((id) => checkedIds.includes(id));
  const isIndeterminate =
    !isChecked && allIds.some((id) => checkedIds.includes(id));

  const handleParentToggle = () => {
    if (isChecked) {
      onToggle(checkedIds.filter((id) => !allIds.includes(id)));
    } else {
      onToggle([...new Set([...checkedIds, ...allIds])]);
    }
  };

  return (
    <Box sx={{ mb: 1 }}>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          bgcolor: "#f5f5f5",
          p: 1,
          borderRadius: 1,
        }}
      >
        <Checkbox
          checked={isChecked}
          indeterminate={isIndeterminate}
          onChange={handleParentToggle}
          size="small"
        />
        <Typography variant="body2" fontWeight="700">
          {node.name}
        </Typography>
      </Box>

      <Box sx={{ ml: 2 }}>
        {node.permissions?.map((p) => (
          <Box
            key={p.id}
            sx={{ display: "flex", alignItems: "center", py: 0.5 }}
          >
            <Checkbox
              checked={checkedIds.includes(p.id)}
              onChange={() =>
                onToggle(
                  checkedIds.includes(p.id)
                    ? checkedIds.filter((id) => id !== p.id)
                    : [...checkedIds, p.id],
                )
              }
              size="small"
            />
            <Typography variant="body2">{p.name}</Typography>
          </Box>
        ))}
      </Box>

      {node.subModules?.map((sub) => (
        <Box key={sub.id} sx={{ ml: 3, borderLeft: "2px solid #ddd", pl: 1 }}>
          <PermissionNode
            node={sub}
            checkedIds={checkedIds}
            onToggle={onToggle}
          />
        </Box>
      ))}
    </Box>
  );
};

// ─── Main Component ───────────────────────────────────────────────────────────

export default function SubRoleDetail() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();

  // parentRole and optionally subRole are passed via navigate state
  const { parentRole, subRole } = location.state || {};
  const isEditing = !!subRole;

  const { filteredTree, loading } = useSelector((state) => state.rbacReducer);

  const [subRoleName, setSubRoleName] = useState(subRole?.name || "");
  const [checkedIds, setCheckedIds] = useState([]);

  useEffect(() => {
    if (!parentRole?.id) {
      // Guard: if no parent role in state, go back
      navigate("/roles-permissions");
      return;
    }
    // Always load the parent-filtered permissions tree
    dispatch(fetchFilteredTree(parentRole.id));

    // If editing, pre-load the sub-role's assigned permissions
    if (isEditing && subRole?.id) {
      dispatch(fetchSubRolePerms(subRole.id)).then((res) => {
        if (res?.success)
          setCheckedIds(res.data?.assignedPermissionIds || []);
      });
    }
  }, []);

  const handleSubmit = () => {
    if (!subRoleName.trim()) return;

    const payload = {
      parentRoleId: parentRole.id,
      name: subRoleName.trim(),
      permissionIds: checkedIds,
    };

    const action = isEditing
      ? dispatch(updateSubRole(subRole.id, payload))
      : dispatch(createSubRole(payload));

    action.then((res) => {
      if (res?.success) {
        toast.success(isEditing ? "Sub-role updated!" : "Sub-role created!");
        navigate("/roles-permissions");
      }
    });
  };

  const handleBack = () => navigate("/roles-permissions");

  return (
    <Box sx={{ p: 3 }}>
      {/* ── Header ──────────────────────────────────────────────────── */}
      <Box
        sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 3 }}
        className="pb20"
      >
        <ArrowBackIosRoundedIcon
          className="font24 colorGrey"
          sx={{ cursor: "pointer" }}
          onClick={handleBack}
        />
        <Typography variant="h5" component="h1" fontWeight="bold">
          {isEditing ? "Update Sub Role" : "Add New Sub Role"}
        </Typography>
      </Box>

      {/* ── Parent Role Info Header ──────────────────────────────────── */}
      <Paper elevation={0} variant="outlined" sx={{ p: 2, mb: 3 }}>
        <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
          <Chip
            label={`Parent Role: ${parentRole?.name}`}
            size="small"
            variant="outlined"
            color="primary"
          />
          <Chip
            label={`Platform: ${parentRole?.platform}`}
            size="small"
            variant="outlined"
          />
          <Chip
            label={`Business Type: ${parentRole?.businessType}`}
            size="small"
            variant="outlined"
          />
          <Chip
            label={`Role Type: ${parentRole?.roleType}`}
            size="small"
            variant="outlined"
          />
        </Box>
      </Paper>

      {/* ── Form ────────────────────────────────────────────────────── */}
      <Grid container spacing={3}>
        <Grid item xs={12} md={8}>
          <Paper
            elevation={0}
            variant="outlined"
            sx={{ p: 3, display: "flex", flexDirection: "column", gap: 3 }}
          >
            {/* Sub Role Name */}
            <TextField
              fullWidth
              label="Sub Role Name"
              value={subRoleName}
              onChange={(e) => setSubRoleName(e.target.value)}
              size="small"
              required
              placeholder="Enter sub role name"
            />

            {/* Permissions Tree */}
            <Box>
              <Typography variant="subtitle1" fontWeight="bold" mb={0.5}>
                Select Permissions
              </Typography>
              <Typography
                variant="caption"
                color="text.secondary"
                mb={1.5}
                display="block"
              >
                Only permissions granted to the parent role are available here.
              </Typography>

              <Box
                sx={{
                  maxHeight: 420,
                  overflowY: "auto",
                  border: "1px solid #e0e0e0",
                  p: 1.5,
                  borderRadius: 1,
                  bgcolor: "#fafafa",
                }}
              >
                {loading && !filteredTree.length ? (
                  <Box
                    sx={{ display: "flex", justifyContent: "center", py: 4 }}
                  >
                    <CircularProgress size={24} />
                  </Box>
                ) : filteredTree.length === 0 ? (
                  <Typography
                    color="text.secondary"
                    textAlign="center"
                    py={3}
                  >
                    No permissions available for this parent role.
                  </Typography>
                ) : (
                  filteredTree.map((mod) => (
                    <PermissionNode
                      key={mod.id}
                      node={mod}
                      checkedIds={checkedIds}
                      onToggle={setCheckedIds}
                    />
                  ))
                )}
              </Box>
            </Box>

            {/* Action Buttons */}
            <Box
              sx={{ display: "flex", justifyContent: "flex-end", gap: 2 }}
            >
              <Button variant="outlined" onClick={handleBack}>
                Cancel
              </Button>
              <Button
                variant="contained"
                onClick={handleSubmit}
                disabled={loading || !subRoleName.trim()}
              >
                Submit
              </Button>
            </Box>
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
}
